/**
 * Contenu ÉPINGLÉ d'un objectif (admin, migration 0514) : programme
 * (`objective_templates.programme_id`, enregistré avec le formulaire), packs
 * (`objective_template_packs`) et SKILLCARDS (`objective_template_cards`).
 * Écriture directe PostgREST sous RLS admin. Tables postérieures aux types
 * générés → client générique.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

export interface ContentProgramme {
  id: string;
  title: string;
  level: string;
  subject: string;
  valid_from: string;
  valid_until: string | null;
}

export interface ContentPack {
  id: string;
  title: string;
  level: string;
  subject: string;
  programme_id: string;
  start_month: number;
  position: number;
}

export interface ObjectiveTemplateContent {
  packIds: string[];
  cardIds: string[];
}

export const objectiveContentCatalogKey = ['admin-objective-content-catalog'] as const;
export const objectiveTemplateContentKey = (templateId: string) =>
  ['admin-objective-template-content', templateId] as const;

/** Programmes et packs publiés : de quoi épingler. */
export function useObjectiveContentCatalog() {
  return useQuery({
    queryKey: objectiveContentCatalogKey,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<{ programmes: ContentProgramme[]; packs: ContentPack[] }> => {
      const [programmes, packs] = await Promise.all([
        db
          .from('curriculum_programmes')
          .select('id, title, level, subject, valid_from, valid_until')
          .order('level')
          .order('subject')
          .order('valid_from', { ascending: false }),
        db
          .from('curriculum_packs')
          .select('id, title, level, subject, programme_id, start_month, position')
          .eq('status', 'published')
          .order('level')
          .order('subject')
          .order('position'),
      ]);
      if (programmes.error) throw new Error(programmes.error.message);
      if (packs.error) throw new Error(packs.error.message);
      return {
        programmes: (programmes.data ?? []) as ContentProgramme[],
        packs: (packs.data ?? []) as ContentPack[],
      };
    },
  });
}

export function useObjectiveTemplateContent(templateId: string) {
  return useQuery({
    queryKey: objectiveTemplateContentKey(templateId),
    enabled: !!templateId,
    queryFn: async (): Promise<ObjectiveTemplateContent> => {
      const [packs, cards] = await Promise.all([
        db
          .from('objective_template_packs')
          .select('pack_id, position')
          .eq('template_id', templateId)
          .order('position'),
        db
          .from('objective_template_cards')
          .select('card_id, position')
          .eq('template_id', templateId)
          .order('position'),
      ]);
      if (packs.error) throw new Error(packs.error.message);
      if (cards.error) throw new Error(cards.error.message);
      return {
        packIds: ((packs.data ?? []) as { pack_id: string }[]).map((r) => r.pack_id),
        cardIds: ((cards.data ?? []) as { card_id: string }[]).map((r) => r.card_id),
      };
    },
  });
}

type ContentKind = 'pack' | 'card';

const TABLES: Record<ContentKind, { table: string; column: string }> = {
  pack: { table: 'objective_template_packs', column: 'pack_id' },
  card: { table: 'objective_template_cards', column: 'card_id' },
};

/** Ajoute ou retire un pack / une SKILLCARD épinglé(e). */
export function useObjectiveContentMutation(templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      kind,
      id,
      action,
      position = 0,
    }: {
      kind: ContentKind;
      id: string;
      action: 'add' | 'remove';
      position?: number;
    }) => {
      const { table, column } = TABLES[kind];
      const res =
        action === 'add'
          ? await db.from(table).insert({ template_id: templateId, [column]: id, position })
          : await db.from(table).delete().eq('template_id', templateId).eq(column, id);
      if (res.error) throw new Error(res.error.message);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: objectiveTemplateContentKey(templateId) }),
  });
}

/** « 3e · Mathématiques — programme en vigueur » pour un sélecteur. */
export function programmeOptionLabel(
  p: Pick<ContentProgramme, 'title' | 'level' | 'subject'>,
  levelLabel: (l: string) => string,
  subjectLabel: (s: string) => string,
): string {
  return `${levelLabel(p.level)} · ${subjectLabel(p.subject)} — ${p.title}`;
}
